import React, { useEffect, useMemo, useState } from 'react';
import { trainingReportApi } from '../../services/trainingReportApi';
import {
  TRAINING_WEEKDAYS,
  buildGeneralDataPayload,
  calculateSchedule,
  createGeneralDataForm,
  minutesToHoursText,
  validateGeneralDataForm,
} from './trainingReportForm';

export const TrainingReportGeneralStep = ({
  idReporte,
  accessToken,
  report,
  setReport,
  onBackToList,
  onNext,
}) => {
  const [form, setForm] = useState(() => createGeneralDataForm(report));
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saveStatus, setSaveStatus] = useState(null);

  const schedule = useMemo(() => calculateSchedule(form), [form]);
  const automaticHours = minutesToHoursText(schedule.totalMinutes);
  const hasAttendance = Boolean(report?.asistencias?.length);
  const isManualDuration = String(form.duracion_horas || '').trim() !== '';

  useEffect(() => {
    setForm(createGeneralDataForm(report));
  }, [report]);

  const clearFieldError = (field) => {
    setFieldErrors((current) => {
      if (!current[field] && !current.schedule) return current;
      const next = { ...current };
      delete next[field];
      delete next.schedule;
      return next;
    });
    setSaveStatus(null);
  };

  const handleFieldChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    clearFieldError(field);
  };

  const toggleWeekday = (weekday) => {
    setForm((current) => {
      const selected = current.dias.includes(weekday);
      return {
        ...current,
        dias: selected
          ? current.dias.filter((item) => item !== weekday)
          : [...current.dias, weekday],
      };
    });
    clearFieldError('dias');
  };

  const saveGeneralData = async () => {
    if (!accessToken || saving) return null;

    const validationErrors = validateGeneralDataForm(form);
    setFieldErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      setSaveStatus({
        type: 'error',
        message: 'Revisa los campos marcados antes de guardar.',
      });
      return null;
    }

    setSaving(true);
    setSaveStatus({ type: 'info', message: 'Guardando datos generales...' });

    try {
      const updatedReport = await trainingReportApi.updateGeneralData(
        idReporte,
        buildGeneralDataPayload(form),
        accessToken,
      );
      setReport(updatedReport);
      setForm(createGeneralDataForm(updatedReport));
      setFieldErrors({});
      setSaveStatus({
        type: 'success',
        message: `Datos guardados. Se calcularon ${updatedReport.sesiones_calculadas ?? schedule.sessionCount} sesión(es) y ${minutesToHoursText(updatedReport.duracion_calculada_minutos ?? schedule.totalMinutes) || '0'} hora(s) de duración automática.`,
      });
      return updatedReport;
    } catch (requestError) {
      console.error('No se pudieron guardar los datos generales:', requestError);
      setSaveStatus({
        type: 'error',
        message:
          requestError?.message ||
          'No fue posible guardar los datos generales del reporte.',
      });
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await saveGeneralData();
  };

  const handleSaveAndContinue = async () => {
    const updated = await saveGeneralData();
    if (updated) onNext();
  };

  return (
    <>
      <div className="admin-operation-panel-heading">
        <div>
          <p className="admin-operation-eyebrow">Paso 1 de 7</p>
          <h2>Datos generales</h2>
          <p>
            Revisa la información tomada de la clase y completa los datos
            específicos del entregable. Todo puede editarse en este reporte sin
            modificar la clase original.
          </p>
        </div>
        <span className={`training-report-status training-report-status-${report?.estado || 'borrador'}`}>
          {report?.estado === 'generado' ? 'Generado' : 'Borrador'}
        </span>
      </div>

      {hasAttendance && (
        <div className="admin-operation-status admin-operation-status-warning training-report-inline-status" role="status">
          Este reporte ya tiene asistencias generadas. Si cambias fechas, días u
          horario, en el siguiente paso tendrás que confirmar la regeneración de
          la cuadrícula de asistencia.
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div className="training-report-general-grid">
          <div className="admin-operation-field training-report-field-wide">
            <label htmlFor="training-report-action">Nombre de la acción de capacitación</label>
            <input
              id="training-report-action"
              value={form.nombre_accion}
              onChange={(event) => handleFieldChange('nombre_accion', event.target.value)}
              disabled={saving}
            />
            {fieldErrors.nombre_accion && <p className="training-report-field-error">{fieldErrors.nombre_accion}</p>}
          </div>

          <div className="admin-operation-field">
            <label htmlFor="training-report-program">Programa</label>
            <input
              id="training-report-program"
              value={form.programa}
              onChange={(event) => handleFieldChange('programa', event.target.value)}
              placeholder="Ej. Supervisión y regulación financiera"
              disabled={saving}
            />
            {fieldErrors.programa && <p className="training-report-field-error">{fieldErrors.programa}</p>}
          </div>

          <div className="admin-operation-field">
            <label htmlFor="training-report-modality">Modalidad</label>
            <input
              id="training-report-modality"
              value={form.modalidad}
              onChange={(event) => handleFieldChange('modalidad', event.target.value)}
              placeholder="Ej. Presencial - En línea"
              disabled={saving}
            />
            {fieldErrors.modalidad && <p className="training-report-field-error">{fieldErrors.modalidad}</p>}
          </div>

          <div className="admin-operation-field training-report-field-wide">
            <label htmlFor="training-report-instructor">Nombre del instructor</label>
            <input
              id="training-report-instructor"
              value={form.nombre_instructor}
              onChange={(event) => handleFieldChange('nombre_instructor', event.target.value)}
              disabled={saving}
            />
            {fieldErrors.nombre_instructor && <p className="training-report-field-error">{fieldErrors.nombre_instructor}</p>}
          </div>

          <div className="admin-operation-field">
            <label htmlFor="training-report-start-date">Fecha de inicio</label>
            <input
              id="training-report-start-date"
              type="date"
              value={form.fecha_inicio}
              onChange={(event) => handleFieldChange('fecha_inicio', event.target.value)}
              disabled={saving}
            />
            {fieldErrors.fecha_inicio && <p className="training-report-field-error">{fieldErrors.fecha_inicio}</p>}
          </div>

          <div className="admin-operation-field">
            <label htmlFor="training-report-end-date">Fecha de término</label>
            <input
              id="training-report-end-date"
              type="date"
              value={form.fecha_fin}
              onChange={(event) => handleFieldChange('fecha_fin', event.target.value)}
              disabled={saving}
            />
            {fieldErrors.fecha_fin && <p className="training-report-field-error">{fieldErrors.fecha_fin}</p>}
          </div>

          <fieldset className="training-report-weekdays training-report-field-wide" disabled={saving}>
            <legend>Días de capacitación</legend>
            <div className="training-report-weekday-grid">
              {TRAINING_WEEKDAYS.map((day) => (
                <label key={day.value} className={form.dias.includes(day.value) ? 'training-report-weekday-selected' : ''}>
                  <input
                    type="checkbox"
                    checked={form.dias.includes(day.value)}
                    onChange={() => toggleWeekday(day.value)}
                  />
                  <span>{day.label}</span>
                </label>
              ))}
            </div>
            {fieldErrors.dias && <p className="training-report-field-error">{fieldErrors.dias}</p>}
          </fieldset>

          <div className="admin-operation-field">
            <label htmlFor="training-report-start-time">Hora de inicio</label>
            <input
              id="training-report-start-time"
              type="time"
              value={form.hora_inicio}
              onChange={(event) => handleFieldChange('hora_inicio', event.target.value)}
              disabled={saving}
            />
            {fieldErrors.hora_inicio && <p className="training-report-field-error">{fieldErrors.hora_inicio}</p>}
          </div>

          <div className="admin-operation-field">
            <label htmlFor="training-report-end-time">Hora de término</label>
            <input
              id="training-report-end-time"
              type="time"
              value={form.hora_fin}
              onChange={(event) => handleFieldChange('hora_fin', event.target.value)}
              disabled={saving}
            />
            {fieldErrors.hora_fin && <p className="training-report-field-error">{fieldErrors.hora_fin}</p>}
          </div>

          {fieldErrors.schedule && (
            <div className="admin-operation-status admin-operation-status-error training-report-field-wide training-report-inline-status">
              {fieldErrors.schedule}
            </div>
          )}

          <div className="training-report-duration-card training-report-field-wide">
            <div>
              <span>Duración automática</span>
              <strong>{automaticHours ? `${automaticHours} h` : 'Pendiente'}</strong>
              <small>
                {schedule.sessionCount > 0
                  ? `${schedule.sessionCount} sesión(es) de ${minutesToHoursText(schedule.minutesPerSession)} h`
                  : 'Completa fechas, días y horario para calcularla.'}
              </small>
            </div>
            <div className="admin-operation-field">
              <label htmlFor="training-report-duration">Duración reportada (horas)</label>
              <input
                id="training-report-duration"
                type="number"
                min="0.01"
                step="0.25"
                value={form.duracion_horas}
                onChange={(event) => handleFieldChange('duracion_horas', event.target.value)}
                placeholder={automaticHours || 'Automática'}
                disabled={saving}
              />
              <small>
                {isManualDuration
                  ? 'Se usará este valor en el documento aunque difiera del cálculo.'
                  : 'Vacío = usar la duración calculada automáticamente.'}
              </small>
              {fieldErrors.duracion_horas && <p className="training-report-field-error">{fieldErrors.duracion_horas}</p>}
              {isManualDuration && (
                <button
                  className="training-report-text-button"
                  type="button"
                  onClick={() => handleFieldChange('duracion_horas', '')}
                  disabled={saving}
                >
                  Restablecer cálculo automático
                </button>
              )}
            </div>
          </div>

          <div className="admin-operation-field">
            <label htmlFor="training-report-coordinator">Nombre del coordinador</label>
            <input
              id="training-report-coordinator"
              value={form.coordinador_nombre}
              onChange={(event) => handleFieldChange('coordinador_nombre', event.target.value)}
              placeholder="Nombre que aparecerá en la firma"
              disabled={saving}
            />
            <small>Será obligatorio antes de generar el PDF definitivo.</small>
          </div>

          <div className="admin-operation-field">
            <label htmlFor="training-report-coordinator-role">Cargo del coordinador</label>
            <input
              id="training-report-coordinator-role"
              value={form.coordinador_cargo}
              onChange={(event) => handleFieldChange('coordinador_cargo', event.target.value)}
              placeholder="Ej. Coordinadora"
              disabled={saving}
            />
            <small>Será obligatorio antes de generar el PDF definitivo.</small>
          </div>
        </div>

        {saveStatus && (
          <div
            className={`admin-operation-status ${saveStatus.type === 'success' ? 'admin-operation-status-success' : saveStatus.type === 'error' ? 'admin-operation-status-error' : 'admin-operation-status-warning'}`}
            role="status"
          >
            {saveStatus.message}
          </div>
        )}

        <div className="training-report-editor-actions">
          <button
            className="admin-operation-button admin-operation-button-secondary"
            type="button"
            onClick={onBackToList}
            disabled={saving}
          >
            Regresar al listado
          </button>
          <button
            className="admin-operation-button admin-operation-button-secondary"
            type="submit"
            disabled={saving}
          >
            {saving ? 'Guardando...' : 'Guardar datos generales'}
          </button>
          <button
            className="admin-operation-button"
            type="button"
            onClick={handleSaveAndContinue}
            disabled={saving}
          >
            {saving ? 'Guardando...' : 'Guardar y continuar'}
          </button>
        </div>
      </form>
    </>
  );
};
